import { createContext, useContext, useMemo, useState } from "react";

type Permission =
  | "freeler:read:all"
  | "freeler:write:all"
  | "freeler:read:summary"
  | "company:admin:read:all"
  | "company:admin:write:all"
  | "user:read:all"
  | "user:write:all"
  | "user:admin:read:all"
  | "user:admin:write:all"
  | "event:admin:read:all"
  | "event:read:all"
  | "event:write:all"
  | "event:write:cancel"
  | "event:admin:write:excuse"
  | "config:read:all"
  | "config:write:all"
  | "payment:read:all"
  | "payment:write:all"
  | "remittance:read:all"
  | "remittance:write:all"
  | "billing:read:all"
  | "billing:admin:read:all"
  | "billing:admin:write:all"
  | "cost-center:read:all"
  | "cost-center:write:all"
  | "cost-center:admin:read:all"
  | "cost-center:admin:write:all"
  | "allowance:admin:read:all"
  | "allowance:read:all"
  | "allowance:write:review"
  | "place:read:all"
  | "place:write:all"
  | "kpi:admin:read:all"
  | "freeler-review:read:all"
  | "freeler-review:write:all"
  | "freeler-review:admin:read:all";

type AvailableRoles =
  | "BACKOFFICE"
  | "BACKOFFICE_OPS"
  | "BACKOFFICE_FINANCIAL"
  | "EXTERNAL_BACKOFFICE"
  | "EXTERNAL_BACKOFFICE_OPS";

interface Role {
  name: AvailableRoles;
  permissions: Permission[];
}

const roles: Role[] = [
  {
    name: "BACKOFFICE",
    permissions: [
      "allowance:admin:read:all",
      "billing:admin:read:all",
      "billing:admin:write:all",
      "company:admin:read:all",
      "company:admin:write:all",
      "config:read:all",
      "config:write:all",
      "cost-center:admin:read:all",
      "cost-center:admin:write:all",
      "event:admin:read:all",
      "event:admin:write:excuse",
      "event:write:cancel",
      "freeler:read:all",
      "freeler-review:admin:read:all",
      "freeler:write:all",
      "kpi:admin:read:all",
      "payment:read:all",
      "payment:write:all",
      "place:read:all",
      "place:write:all",
      "remittance:read:all",
      "remittance:write:all",
      "user:admin:read:all",
      "user:admin:write:all"
    ],
  },
  {
    name: "BACKOFFICE_OPS",
    permissions: [
      "allowance:admin:read:all",
      "company:admin:read:all",
      "company:admin:write:all",
      "config:read:all",
      "config:write:all",
      "event:admin:read:all",
      "event:admin:write:excuse",
      "event:write:cancel",
      "freeler:read:all",
      "freeler-review:admin:read:all",
      "freeler:write:all",
      "place:read:all",
      "place:write:all",
      "user:admin:read:all",
      "user:admin:write:all"
    ],
  },
  {
    name: "BACKOFFICE_FINANCIAL",
    permissions: [
      "freeler:read:all",
      "company:admin:read:all",
      "user:admin:read:all",
      "event:admin:read:all",
      "billing:admin:read:all",
      "billing:admin:write:all",
      "payment:read:all",
      "payment:write:all",
      "remittance:read:all",
      "remittance:write:all"
    ],
  },
  {
    name: "EXTERNAL_BACKOFFICE",
    permissions: [
      "allowance:read:all",
      "allowance:write:review",
      "billing:read:all",
      "cost-center:read:all",
      "cost-center:write:all",
      "event:read:all",
      "event:write:all",
      "event:write:cancel",
      "freeler:read:summary",
      "freeler-review:read:all",
      "freeler-review:write:all",
      "place:read:all",
      "user:read:all",
      "user:write:all",
    ],
  },
  {
    name: "EXTERNAL_BACKOFFICE_OPS",
    permissions: [
      "allowance:read:all",
      "cost-center:read:all",
      "event:read:all",
      "event:write:all",
      "event:write:cancel",
      "freeler:read:summary",
      "freeler-review:read:all",
      "freeler-review:write:all",
      "place:read:all"
    ],
  },
];

type PermissionsProviderState = {
  permissions: string[];
  userRole: AvailableRoles | null;
  setPermissions: (permissions: string[]) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
};

const initialState: PermissionsProviderState = {
  permissions: [],
  userRole: null,
  setPermissions: () => null,
  isLoading: true,
  setIsLoading: () => null,
};

const PermissionsProviderContext = createContext<PermissionsProviderState>(initialState);

const PermissionsProvider = ({ children }: { children: React.ReactNode }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  function getRoleByPermissions(permissions: Permission[]): AvailableRoles | null {
    for (const role of roles) {
      if (role.permissions.every((permission) => permissions.includes(permission))) {
        return role.name;
      }
    }
    return null;
  }

  const userRole = useMemo(() => {
    const userRole = getRoleByPermissions(permissions as Permission[]);
    return userRole;
  }, [permissions]);

  return (
    <PermissionsProviderContext.Provider
      value={{ permissions, userRole, setPermissions, isLoading, setIsLoading }}
    >
      {children}
    </PermissionsProviderContext.Provider>
  );
};

/**
 * Permissions provider hook.
 * @returns - permissions and setPermissions function.
 */
const usePermissions = () => {
  const context = useContext(PermissionsProviderContext);

  if (context === undefined)
    throw new Error("usePermissions must be used within a PermissionsProvider");

  return context;
};

export { PermissionsProviderContext, PermissionsProvider, usePermissions, type AvailableRoles };
